import type { TicketDto } from '@planuze/admin-contracts';
import {
  InlineAlert,
  Spinner,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@planuze/ui';
import { type ReactNode, useState } from 'react';
import { useSupportContent } from '../../content/sections/support.js';
import { useCompanyDetail } from '../../hooks/use-companies.js';
import { useUserDetail } from '../../hooks/use-users.js';
import { fmtDateTime } from '../../lib/format.js';
import {
  statusLabel as companyStatusLabel,
  statusTone as companyStatusTone,
} from '../companies/company-status.js';
import { priorityTone, statusLabel, statusTone } from './ticket-status.js';

type PanelTab = 'ticket' | 'user' | 'org';

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

function PanelLoading({ ariaLabel }: { readonly ariaLabel: string }) {
  return (
    <div className="grid place-items-center py-8">
      <Spinner aria-label={ariaLabel} />
    </div>
  );
}

/* Painel lateral de contexto do chat de suporte (ADR-0704). A aba Ticket usa o
   `ticketDetailDto` JÁ carregado (sem query nova); Usuário e Organização são LAZY — cada
   query só dispara quando a aba fica ativa (`enabled`), o que evita duas chamadas extra por
   ticket aberto. Não toca no ChatThread/ChatComposer/websocket. */
export function TicketContextPanel({ ticket }: { readonly ticket: TicketDto }) {
  const copy = useSupportContent();
  const [tab, setTab] = useState<PanelTab>('ticket');

  const userQuery = useUserDetail(ticket.userId, { enabled: tab === 'user' });
  const orgQuery = useCompanyDetail(ticket.orgId ?? '', {
    enabled: tab === 'org' && ticket.orgId !== null,
  });

  const renderUser = (): ReactNode => {
    if (userQuery.isLoading) return <PanelLoading ariaLabel={copy.contextPanel.loadingAriaLabel} />;
    if (userQuery.isError)
      return <InlineAlert variant="destructive">{copy.contextPanel.userLoadError}</InlineAlert>;
    const user = userQuery.data;
    if (user === undefined)
      return <p className="text-sm text-muted-foreground">{copy.contextPanel.userUnavailable}</p>;
    return (
      <dl className="space-y-4">
        <Field label={copy.contextPanel.fieldEmail}>{user.email}</Field>
        <Field label={copy.contextPanel.fieldName}>{user.name ?? '—'}</Field>
        <Field label={copy.contextPanel.fieldOrgs}>{user.orgs.length}</Field>
        <Field label={copy.contextPanel.fieldLicenses}>{user.licenses.length}</Field>
      </dl>
    );
  };

  const renderOrg = (): ReactNode => {
    if (ticket.orgId === null) {
      return (
        <p className="text-sm text-muted-foreground">
          {copy.contextPanel.orgIndividualPlaceholder}
        </p>
      );
    }
    if (orgQuery.isLoading) return <PanelLoading ariaLabel={copy.contextPanel.loadingAriaLabel} />;
    if (orgQuery.isError)
      return <InlineAlert variant="destructive">{copy.contextPanel.orgLoadError}</InlineAlert>;
    const org = orgQuery.data;
    if (org === undefined)
      return <p className="text-sm text-muted-foreground">{copy.contextPanel.orgUnavailable}</p>;
    return (
      <dl className="space-y-4">
        <Field label={copy.contextPanel.fieldName}>{org.name}</Field>
        <Field label={copy.contextPanel.fieldStatus}>
          <StatusBadge variant={companyStatusTone(org.status)}>
            {companyStatusLabel(org.status)}
          </StatusBadge>
        </Field>
        <Field label={copy.contextPanel.fieldMembers}>{org.memberCount}</Field>
        <Field label={copy.contextPanel.fieldSubscription}>{org.subscription?.status ?? '—'}</Field>
      </dl>
    );
  };

  return (
    <aside className="hidden w-80 shrink-0 flex-col border-l border-border bg-card lg:flex">
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as PanelTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="border-b border-border px-3 pt-3">
          <TabsList variant="underline">
            <TabsTrigger value="ticket">{copy.contextPanel.tabTicket}</TabsTrigger>
            <TabsTrigger value="user">{copy.contextPanel.tabUser}</TabsTrigger>
            <TabsTrigger value="org">{copy.contextPanel.tabOrg}</TabsTrigger>
          </TabsList>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <TabsContent value="ticket">
            <dl className="space-y-4">
              <Field label={copy.contextPanel.fieldSubject}>{ticket.subject}</Field>
              <Field label={copy.contextPanel.fieldStatus}>
                <StatusBadge variant={statusTone(ticket.status)}>
                  {statusLabel(ticket.status, copy.ticketStatusLabels)}
                </StatusBadge>
              </Field>
              <Field label={copy.contextPanel.fieldPriority}>
                <StatusBadge variant={priorityTone(ticket.priority)}>{ticket.priority}</StatusBadge>
              </Field>
              <Field label={copy.contextPanel.fieldCreatedAt}>
                {fmtDateTime(ticket.createdAt)}
              </Field>
              <Field label={copy.contextPanel.fieldAssignedTo}>
                {ticket.assignedToStaffId ?? copy.contextPanel.noAssignee}
              </Field>
              <Field label={copy.contextPanel.fieldOrigin}>
                {ticket.orgId === null
                  ? copy.contextPanel.originIndividual
                  : copy.contextPanel.originOrg}
              </Field>
            </dl>
          </TabsContent>
          <TabsContent value="user">{renderUser()}</TabsContent>
          <TabsContent value="org">{renderOrg()}</TabsContent>
        </div>
      </Tabs>
    </aside>
  );
}
